// @ts-check
import ZeroDataGrid from '~/components/Shared/ZeroDataGrid/ZeroDataGrid';
import { useDataGridSetup } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import useZeroSelector from '~/hooks/useZeroSelector';
import { objectIsEmpty, setGridLoading } from '~/other/Helper';
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useIncidentDataGridContext } from '~/components/Incidents/IncidentDataGrid/IncidentDataGrid.context';
import { EventNames, getColumnDefs, loadIncidents } from '~/components/Incidents/IncidentDataGrid/IncidentDataGrid.utils';

const INCIDENTS_PER_PAGE = 25;

export default function IncidentDataGrid() {
    const location = useLocation();
    const {gridRef, settings, setSettings, rowCount, incidentOptions, customFields, isLoading} = useIncidentDataGridContext();
    const filterQuery = useZeroSelector(state => state.incidents.filterQuery);
    const periodQuery = useZeroSelector(state => state.incidents.periodQuery);

    const colDefs = useMemo(() => getColumnDefs(incidentOptions, customFields), [incidentOptions, customFields]);

    const getRows = useCallback(async (/** @type {AgGrid.IGetRowsParams} */params) => {
        const setLoading = (isLoading) => {
            if (rowCount === null) {
                setGridLoading(gridRef.current?.api, isLoading);
            }
        }

        const currentPage = Math.floor((params.startRow ?? 0) / INCIDENTS_PER_PAGE) + 1;

        const queryParams = new URLSearchParams(filterQuery + periodQuery);
        queryParams.set('per_page', String(INCIDENTS_PER_PAGE));
        queryParams.set('page', String(currentPage));

        if (!objectIsEmpty(params.filterModel)) {
            queryParams.set('ag_filter', JSON.stringify(params.filterModel));
        }

        params.sortModel.forEach(sortModel => {
            let col = sortModel.colId;
            if (sortModel.sort === 'desc') {
                col = `-${col}`;
            }
            queryParams.append('sort_by', col);
        });

        setLoading(true);
        const {incidents, totalIncidents} = await loadIncidents(queryParams);
        params.successCallback(incidents, totalIncidents);
        setLoading(false);
    }, [rowCount, filterQuery, periodQuery]);

    const {onGridReady, onStateUpdated} = useDataGridSetup({
        eventNames: EventNames,
        autoLoadData: true,
        gridRef,
        colDefs,
        getRows,
        setSettings,
    });

    if (isLoading) {
        return null;
    }

    return (
        <ZeroDataGrid
            gridRef={gridRef}
            columnDefs={colDefs}
            defaultColDef={{
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                }
            }}
            initialState={location.state?.gridState ?? settings}
            onGridReady={onGridReady}
            onStateUpdated={onStateUpdated}
            cacheBlockSize={INCIDENTS_PER_PAGE}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={2}
            infiniteInitialRowCount={rowCount ?? INCIDENTS_PER_PAGE}
            pagination={true}
            paginationPageSizeSelector={false}
            paginationPageSize={rowCount ?? INCIDENTS_PER_PAGE}
            tooltipShowDelay={500}
            tooltipShowMode="standard"
            suppressCellFocus
        />
    );
}
